import { AxiosError } from 'axios';
import { HttpStatus } from 'constants/http-status';
import { GeneralError, SubscriptionErrors } from 'interfaces/errors';
import { PdProductItem } from 'interfaces/line-item';
import { SubscriptionErrorMessageKey } from 'modules/account/pages/subscription/constants';
import { axios, isAxiosError } from 'utils/axios';
import {
  SubscriptionDetails,
  SubscriptionDetailsWithEffectiveCart,
  SubscriptionShortDetails,
  UpdateSubscriptionDetailsInput,
} from '../interfaces/subscription';
import * as SubscriptionModel from '../models/subscription';
import { showApiErrorToast } from './axios-interceptors';

export const fetchCustomerSubscriptions = (
  customerId: number
): Promise<Array<SubscriptionShortDetails>> =>
  axios
    .get(`/account/subscriptions/${customerId}`)
    .then(({ data }) => data.subscriptions)
    .then((subscriptions) =>
      subscriptions.map(SubscriptionModel.subscriptionShortDetailsFromServerFormat)
    );

export const fetchSubscriptionDetailsWithEffectiveCart = (
  customerId: number,
  subscriptionId: string
): Promise<SubscriptionDetailsWithEffectiveCart> =>
  axios
    .get(`/account/subscriptions/${customerId}/${subscriptionId}`, {
      withoutAudience: true,
      withoutNinetailed: true,
    })
    .then(({ data }) => data.details)
    .then(({ subscription, effectiveCart, oos }) => ({
      subscription: SubscriptionModel.subscriptionDetailsFromServerFormat(subscription),
      effectiveCart,
      oos,
    }));

/**
 * imported from backend repo file : src/_types/subscription.ts
 */
export interface OneOffPurchaseInput {
  title?: string;
  handle?: string;
  price: number; //float value, not PriceInt
  quantity: number;
  variantId: string | number;
  productId: string | number;
  masterProductId: string | number;
  masterVariantId: string | number;
  image?: string;
}

/**
 * Add one-time purchase item to an existing subscription.
 * @TODO : API implementation is pretty inconstant in response interface (subscription details => oneOffPurchaseItems)
 * API returns only returns what is given without validation, other than mandatory in OneOffPurchaseInput field above.
 * If developer forget to assign, handle or title, response doesn't return them.
 * Do not relay on the response.
 * */
export const addOneOffItemToSubscription = (
  subscriptionId: string,
  oneOffPurchaseItems: OneOffPurchaseInput
): Promise<SubscriptionDetails> =>
  axios
    .post(`/account/subscriptions/addOneOffPurchase/${subscriptionId}`, {
      oneOffPurchaseItems,
    })
    .then(({ data }) => data);

/**
 * change quantity oneOffPurchase product
 * @param {string} subscriptionId The id of the subscription.
 * @param {number} variantId The id of the variant product.
 * @returns {Promise}
 */

export const updateOneOffPurchase = (
  subscriptionId: string,
  variantId: number,
  oneOffPurchaseItem: OneOffPurchaseInput
): Promise<PdProductItem> =>
  axios
    .put(`/account/subscriptions/${subscriptionId}/oneOffPurchase/${variantId}`, {
      oneOffPurchaseItems: { ...oneOffPurchaseItem },
    })
    .then(({ data }) => data);

/**
 * delete oneOffPurchase product
 * @param {string} subscriptionId The id of the subscription.
 * @param {number} variantId The id of the variant product.
 * @returns {Promise}
 */

export const deleteOneOffPurchase = (
  subscriptionId: string,
  variantId: number
): Promise<PdProductItem> =>
  axios
    .delete(`/account/subscriptions/${subscriptionId}/oneOffPurchase/${variantId}`)
    .then(({ data }) => data.details);

interface SubscriptionUpdateInfo {
  /** MOV threshold for subscription */
  threshold: number;
  /** Required amount to reach MOV */
  required: number;
}
export interface SubscriptionMovError extends GeneralError<
  SubscriptionUpdateInfo,
  typeof SubscriptionErrors.UPDATE_ERROR,
  typeof SubscriptionErrorMessageKey.SubscriptionMovError
> {}

export interface CustomAxiosError extends AxiosError<{ error: SubscriptionMovError }> {}

export function isSubscriptionMovError(obj: any): obj is SubscriptionMovError {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'name' in obj &&
    obj['name'] === SubscriptionErrors.UPDATE_ERROR &&
    'message' in obj &&
    obj['message'] === SubscriptionErrorMessageKey.SubscriptionMovError
  );
}

const subscriptionMovErrorHandler = (error) => {
  const _error = error as CustomAxiosError;
  if (isSubscriptionMovError(_error.response?.data.error)) {
    throw _error.response?.data.error;
  }
  // On this point error is always axios error
  // This condition is just for the sake of type assertion
  else if (isAxiosError(error)) {
    showApiErrorToast({ error });
    throw error;
  }
};

/**
 * Update subscription
 * This api might throw an error when MOV is not met.
 */
export const updateSubscription = (
  subscriptionId: string,
  subscriptionUpdate: Partial<UpdateSubscriptionDetailsInput>
) =>
  axios
    .post(
      `/account/subscriptions/updateSubscription/${subscriptionId}`,
      SubscriptionModel.subscriptionDetailsToServerFormat({
        id: subscriptionId,
        ...subscriptionUpdate,
      }),
      {
        errorHandling: {
          statusesToOmit: [HttpStatus.UNPROCESSABLE_CONTENT],
        },
      }
    )
    .then(({ data }) => data)
    .then(SubscriptionModel.subscriptionDetailsFromServerFormat)
    .catch(subscriptionMovErrorHandler);

interface SubscriptionProductStatus {
  variantId: string;
  active: boolean;
}

/**
 * fetch inactive product
 * @param {string} subscriptionId The id of the subscription.
 * @param {number} variantId The id of the variant product.
 * @param {boolean} active The status of the product.
 * @returns {Promise}
 */
export const markProductAsInactive = (
  subscriptionId: string,
  variantId: number
): Promise<
  | SubscriptionProductStatus
  | {
      error: 'SubThresholdNotMeet';
    }
  | undefined
> =>
  axios
    .post(
      `/account/subscription/subscriptionChangeProductStatus/${subscriptionId}`,
      {
        variantId: variantId,
        active: false,
      },
      {
        errorHandling: {
          statusesToOmit: [HttpStatus.UNPROCESSABLE_CONTENT],
        },
      }
    )
    .then(({ data }) => data)
    .catch(subscriptionMovErrorHandler);
/**
 * fetch recovered product
 * @param {string} subscriptionId The id of the subscription.
 * @param {number} variantId The id of the variant product.
 * @param {boolean} active The status of the product.
 * @returns {Promise}
 */

export const recoverSubscriptionItem = (
  subscriptionId: string,
  variantId: number
): Promise<SubscriptionProductStatus> =>
  axios
    .post(`/account/subscription/subscriptionChangeProductStatus/${subscriptionId}`, {
      variantId: variantId,
      active: true,
    })
    .then(({ data }) => data);

interface DeliverSubOnDemandResponse {
  subscriptionId: string;
  /** DB TTL value. Frontend doesn't have to refer to this property */
  ttl: number;
}

/**
 * Deliver subscription on demand before next deliver date
 * @param {string} subscriptionId The id of the subscription.
 */
export const deliverSubOnDemand = ({
  subscriptionId,
}: {
  subscriptionId: string;
}): Promise<DeliverSubOnDemandResponse> =>
  axios.get(`/subscription/deliver-now/${subscriptionId}`).then(({ data }) => data);
